/**
 * ELF-CIRCLE merchandise — section `#8c`, "Shop the drop".
 *
 * Category chips filter the grid, hovering a card slides its size row up, and
 * picking a size drops the item straight into the sticky bag on the right.
 * Sold-out sizes stay visible but struck through, matching the design.
 *
 * Only the section's own chrome is admin-editable. The catalogue below —
 * product names, prices, descriptions, sizes and imagery — is the seam where
 * Shopify plugs in, so none of it is overridable: an admin edit would be
 * silently overwritten by the next catalogue sync.
 */

import React, { useState } from 'react';
import EditableImage from './EditableImage';
import EditableText from './EditableText';
import hoodieBlack from '../../../../assets/elf/merch-hoodie-black.jpg';
import hoodieBlue from '../../../../assets/elf/merch-hoodie-blue.jpg';
import hoodieGrey from '../../../../assets/elf/merch-hoodie-grey.jpg';
import teeAllOver from '../../../../assets/elf/merch-tee-allover.jpg';
import teeBlack from '../../../../assets/elf/merch-tee-black.jpg';
import teeWhite from '../../../../assets/elf/merch-tee-white.jpg';

type MerchCategory = 'Tees' | 'Hoodies';

interface Product {
  /** Stable identity — becomes the Shopify product handle. */
  handle: string;
  name: string;
  category: MerchCategory;
  image: string;
  price: number;
  desc: string;
  tag?: string;
  /** Sizes offered, in order; `false` means sold out. */
  sizes: { label: string; inStock: boolean }[];
}

const ALL_SIZES = ['S', 'M', 'L', 'XL'];

/** Build a size row from the sizes that are still in stock. */
const sizes = (...inStock: string[]) =>
  ALL_SIZES.map((label) => ({ label, inStock: inStock.includes(label) }));

/** Placeholder catalogue — replaced wholesale by the Shopify integration. */
const PRODUCTS: Product[] = [
  {
    handle: 'circuit-hoodie',
    name: 'Circuit Hoodie',
    category: 'Hoodies',
    image: hoodieBlack,
    price: 98,
    desc: 'Black · heavyweight fleece',
    tag: 'New drop',
    sizes: sizes('S', 'M', 'L'),
  },
  {
    handle: 'signal-hoodie',
    name: 'Signal Hoodie',
    category: 'Hoodies',
    image: hoodieBlue,
    price: 105,
    desc: 'Slate blue · sleeve print',
    sizes: sizes('S', 'M', 'L', 'XL'),
  },
  {
    handle: 'crest-hoodie',
    name: 'Crest Hoodie',
    category: 'Hoodies',
    image: hoodieGrey,
    price: 88,
    desc: 'Heather grey · cuff badge',
    tag: 'Low stock',
    sizes: sizes('S', 'M'),
  },
  {
    handle: 'circuit-tee',
    name: 'Circuit Tee',
    category: 'Tees',
    image: teeBlack,
    price: 44,
    desc: 'Black · front crest',
    sizes: sizes('S', 'M', 'L', 'XL'),
  },
  {
    handle: 'signal-tee',
    name: 'Signal Tee',
    category: 'Tees',
    image: teeWhite,
    price: 38,
    desc: 'White · oversized cut',
    sizes: sizes('S'),
  },
  {
    handle: 'all-over-tee',
    name: 'All-Over Tee',
    category: 'Tees',
    image: teeAllOver,
    price: 52,
    desc: 'Full-print circuit pattern',
    tag: 'Limited',
    sizes: sizes('S', 'M', 'L', 'XL'),
  },
];

const FILTERS: ('All' | MerchCategory)[] = ['All', 'Tees', 'Hoodies'];

const SECTION = {
  eyebrow: 'Merch & commerce',
  title: 'Shop the drop',
  lede:
    'Filter on the left, pick a size on the card, and the bag on the right updates in place. Checkout is one button, and the email capture rides along with it.',
  bagLabel: 'Your bag',
  bagEmpty: 'Pick a size on any card to start a bag.',
  checkout: 'Checkout',
  pay: 'Card, Apple Pay or Klarna',
};

interface BagItem {
  /** Monotonic key — the same product/size can be added more than once. */
  id: number;
  name: string;
  size: string;
  price: number;
  image: string;
}

export const ElfMerch: React.FC = () => {
  const [filter, setFilter] = useState<'All' | MerchCategory>('All');
  const [bag, setBag] = useState<BagItem[]>([]);
  const [picked, setPicked] = useState<Record<string, string>>({});
  const [nextId, setNextId] = useState(0);

  const addToBag = (product: Product, size: string) => {
    setPicked((prev) => ({ ...prev, [product.handle]: size }));
    setBag((prev) => [
      ...prev,
      { id: nextId, name: product.name, size, price: product.price, image: product.image },
    ]);
    setNextId((n) => n + 1);
  };

  const removeFromBag = (id: number) => setBag((prev) => prev.filter((it) => it.id !== id));

  const subtotal = bag.reduce((sum, it) => sum + it.price, 0);

  return (
    <section id="shop" className="elf-band elf-shop">
      <EditableImage slot="shop.backdrop" className="elf-shop__bg" alt="" />
      <div className="elf-shop__scrim" />
      <div className="sigrule elf-band__rule" />
      <div className="elf-band__inner">
        <div className="elf-section__head" style={{ marginBottom: 30 }}>
          <div style={{ maxWidth: 620 }}>
            <span className="eyebrow an" style={{ animationDelay: '.05s' }}>
              <i />
              <EditableText contentKey="merch.eyebrow" fallback={SECTION.eyebrow} label="Eyebrow" />
            </span>
            <EditableText
              as="h2"
              contentKey="merch.title"
              fallback={SECTION.title}
              label="Section title"
              className="an elf-section__title"
              style={{ animationDelay: '.12s' }}
            />
            <EditableText
              as="p"
              contentKey="merch.lede"
              fallback={SECTION.lede}
              label="Section intro"
              multiline
              className="an elf-section__lede"
              style={{ animationDelay: '.18s' }}
            />
          </div>
          <div className="an" style={{ animationDelay: '.24s' }}>
            <div className="elf-merch__filters">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  type="button"
                  className={f === filter ? 'fbtn on' : 'fbtn'}
                  onClick={() => setFilter(f)}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="elf-merch__layout">
          <div className="elf-merch__grid">
            {PRODUCTS.map((product) => (
              <div
                key={product.handle}
                className="prod"
                style={{
                  display: filter === 'All' || product.category === filter ? undefined : 'none',
                }}
              >
                <div className="prodimg">
                  <img src={product.image} alt={product.name} />
                  {product.tag && <span className="prodtag">{product.tag}</span>}
                  <div className="prodadd">
                    {product.sizes.map((size) => (
                      <button
                        key={size.label}
                        type="button"
                        className={picked[product.handle] === size.label ? 'sz on' : 'sz'}
                        disabled={!size.inStock}
                        aria-label={`${product.name}, size ${size.label}`}
                        onClick={() => addToBag(product, size.label)}
                      >
                        {size.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="elf-prod__body">
                  <div className="elf-prod__row">
                    <span className="elf-prod__name">{product.name}</span>
                    <span className="elf-prod__price">${product.price}</span>
                  </div>
                  <div className="elf-prod__desc">{product.desc}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="scard elf-bag">
            <div className="elf-bag__head">
              <EditableText
                as="div"
                contentKey="merch.bag.label"
                fallback={SECTION.bagLabel}
                label="Bag heading"
                className="mono"
              />
              <span className="elf-bag__count">
                <b>{bag.length}</b> items
              </span>
            </div>

            {bag.length > 0 ? (
              <div className="elf-bag__list">
                {bag.map((item) => (
                  <div className="cartrow" key={item.id}>
                    <img src={item.image} alt="" />
                    <div>
                      <div className="elf-cartrow__name">{item.name}</div>
                      <div className="elf-cartrow__meta">
                        Size {item.size} · ${item.price}
                      </div>
                    </div>
                    <button
                      className="chipx"
                      type="button"
                      aria-label={`Remove ${item.name}, size ${item.size}`}
                      onClick={() => removeFromBag(item.id)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <EditableText
                as="div"
                contentKey="merch.bag.empty"
                fallback={SECTION.bagEmpty}
                label="Empty bag message"
                multiline
                className="elf-bag__empty"
              />
            )}

            <div className="elf-bag__sub">
              <span>Subtotal</span>
              <span className="elf-bag__total">${subtotal}</span>
            </div>
            <input className="fld" placeholder="Email or mobile #" aria-label="Email or mobile" />
            <button className="pillcta mag" type="button">
              <EditableText
                contentKey="merch.cta.checkout"
                fallback={SECTION.checkout}
                label="Checkout button"
              />{' '}
              <span>→</span>
            </button>
            <EditableText
              as="div"
              contentKey="merch.pay"
              fallback={SECTION.pay}
              label="Payment note"
              className="elf-bag__pay"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ElfMerch;
